import React, { useState } from 'react';
import { Lightbox } from './Lightbox';

/**
 * Gallery — 상세페이지 사진 갤러리 (2+1 모자이크 + 도트 + lightbox).
 *
 * 정책 무관 (이미지 sanitization은 호출부 책임).
 * 모바일 스와이프 lightbox 포함.
 */

export interface GalleryImage {
  src: string;
  alt?: string;
}

/**
 * Gallery layout (Phase 6.5):
 *   - 'mosaic-2+1' : 2+1 모자이크 (기본, 모바일) — Hero 좌측 큰 박스 + 우측 2칸
 *   - 'grid-3'     : 3열 그리드
 *   - 'grid-4'     : 4열 그리드
 *   - 'responsive' : 모바일 mosaic-2+1 → md:3열 → lg:4열 (시안 .r-gallery 매핑)
 */
export type GalleryLayout = 'mosaic-2+1' | 'grid-3' | 'grid-4' | 'responsive';

export interface GalleryProps {
  images: GalleryImage[];
  /** 4장 이상이면 마지막 칸에 "+N" 오버레이 */
  maxPreview?: number;
  /** 자료 부족 case — 회색조 */
  sparse?: boolean;
  /** lightbox 비활성 (e.g. 미리보기 전용) */
  disableLightbox?: boolean;
  /** 그리드 레이아웃 — 기본 'mosaic-2+1' (모바일 회귀 보장) */
  layout?: GalleryLayout;
  className?: string;
}

const GALLERY_CONTAINER: Record<GalleryLayout, string> = {
  'mosaic-2+1': 'grid grid-cols-2 gap-1.5',
  'grid-3':     'grid grid-cols-3 gap-2',
  'grid-4':     'grid grid-cols-4 gap-2',
  // 반응형: 모바일 모자이크 → md 3열 → lg 4열
  responsive:   'grid grid-cols-2 gap-1.5 md:grid-cols-3 md:gap-3 lg:grid-cols-4 lg:gap-3.5',
};

export const Gallery: React.FC<GalleryProps> = ({
  images,
  maxPreview = 3,
  sparse,
  disableLightbox,
  layout = 'mosaic-2+1',
  className = '',
}) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // 반응형 모드는 첫 칸 hero 효과 + md+에서 3-4열이라 더 많이 미리보기
  const effectiveMax =
    layout === 'grid-3' ? Math.max(maxPreview, 6)
    : layout === 'grid-4' ? Math.max(maxPreview, 8)
    : layout === 'responsive' ? Math.max(maxPreview, 6)
    : maxPreview;
  const preview = images.slice(0, effectiveMax);
  const remainder = Math.max(0, images.length - effectiveMax);

  if (preview.length === 0) return null;

  const open = (idx: number) => !disableLightbox && setLightboxIndex(idx);
  const close = () => setLightboxIndex(null);

  const isMosaicMode = layout === 'mosaic-2+1' || layout === 'responsive';

  return (
    <>
      <div className={`${GALLERY_CONTAINER[layout]} ${className}`}>
        {preview.map((img, i) => {
          const isLast = i === preview.length - 1 && remainder > 0;
          // mosaic 효과: 첫 칸이 hero (모바일·responsive 모드에서만, md+는 일반 그리드)
          const isHero = isMosaicMode && i === 0 && preview.length > 1;
          return (
            <button
              key={i}
              type="button"
              onClick={() => open(i)}
              className={`relative overflow-hidden rounded-ca-2xl bg-block aspect-[4/3] ${
                isHero
                  ? layout === 'responsive'
                    ? 'col-span-2 row-span-2 aspect-[16/10] md:col-span-1 md:row-span-1 md:aspect-[4/3]'
                    : 'col-span-2 row-span-2 aspect-[16/10]'
                  : ''
              }`}
            >
              <img
                src={img.src}
                alt={img.alt ?? ''}
                loading="lazy"
                className={`h-full w-full object-cover ${sparse ? 'grayscale opacity-70' : ''}`}
              />
              {isLast && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white text-[18px] font-extrabold">
                  +{remainder}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {lightboxIndex != null && (
        <Lightbox
          images={images}
          startIndex={lightboxIndex}
          onClose={close}
        />
      )}
    </>
  );
};

export default Gallery;
